import {
  CreateBrokerPayloadDto,
  ReplaceBrokerPayloadDto,
  ReplaceBrokerPayloadDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import * as yup from 'yup';
import { TFunction } from 'i18next';
import { isStrictEmail } from 'utils';

export const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    status: yup
      .string()
      .required(t('validation.required', { field: t('general.status') })),
    name: yup
      .string()
      .trim()
      .required(t('validation.required', { field: t('broker.brokerName') })),
    legalEntityName: yup
      .string()
      .trim()
      .required(
        t('validation.required', { field: t('broker.brokerLegalEntityName') })
      ),
    group: yup
      .string()
      .required(t('validation.required', { field: t('broker.brokerGroup') })),
    type: yup
      .string()
      .required(t('validation.required', { field: t('broker.brokerType') })),
    brokerEmail: yup
      .string()
      .email(t('validation.invalid', { field: t('general.email') }))
      .test(
        'is-strict-email',
        t('validation.invalid', { field: t('general.email') }),
        (value) => !value || isStrictEmail(value)
      )
      .optional(),
    claimsEmail: yup
      .string()
      .nullable()
      .email(t('validation.invalid', { field: t('general.email') }))
      .test(
        'is-strict-email',
        t('validation.invalid', { field: t('general.email') }),
        (value) => !value || isStrictEmail(value)
      ),
    financeEmail: yup
      .string()
      .nullable()
      .email(t('validation.invalid', { field: t('general.email') }))
      .test(
        'is-strict-email',
        t('validation.invalid', { field: t('general.email') }),
        (value) => !value || isStrictEmail(value)
      ),
    phoneNumber: yup
      .string()
      .nullable()
      .test(
        'empty-or-valid-phone',
        t('validation.invalid', { field: t('general.phoneNumberLower') }),
        (value) =>
          value === '' || !value || /^\+(\d{1,4})\s?\d{7,15}$/.test(value)
      ),
    website: yup
      .string()
      .nullable()
      .test(
        'empty-or-valid-url',
        t('validation.invalid', { field: t('general.url') }),
        (value) =>
          value === '' ||
          !value ||
          /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(value)
      ),
    address: yup.string().nullable(),
    country: yup
      .string()
      .required(t('validation.required', { field: t('general.country') })),
    city: yup.string().nullable(),
    postcode: yup.string().nullable(),
    hasToba: yup
      .boolean()
      .required(t('validation.required', { field: t('broker.tobaStatus') })),
    hasKyc: yup
      .boolean()
      .required(t('validation.required', { field: t('broker.kycStatus') })),
    tobaCompletionDate: yup.date().when('hasToba', {
      is: true,
      then: () =>
        yup.date().required(
          t('validation.required', {
            field: t('broker.tobaLastCompletionDate'),
          })
        ),
      otherwise: () => yup.date().nullable(),
    }),
    kycCompletionDate: yup.date().when('hasKyc', {
      is: true,
      then: () =>
        yup.date().required(
          t('validation.required', {
            field: t('broker.kycLastCompletionDate'),
          })
        ),
      otherwise: () => yup.date().nullable(),
    }),
  });

export const initialValues = {
  hasToba: undefined,
  hasKyc: undefined,
  tobaCompletionDate: undefined,
  kycCompletionDate: undefined,
  status: ReplaceBrokerPayloadDtoStatusEnum.Active,
  name: undefined,
  group: undefined,
  type: undefined,
  brokerEmail: undefined,
  claimsEmail: undefined,
  financeEmail: undefined,
  phoneNumber: undefined,
  website: undefined,
  address: undefined,
  country: undefined,
  city: undefined,
  postcode: undefined,
  state: undefined,
  legalEntityName: undefined,
};

export const fields = Object.keys(initialValues) as (
  | keyof CreateBrokerPayloadDto
  | keyof ReplaceBrokerPayloadDto
)[];
